/**
 * Klaviyo Quote Request Handler
 * Handles quote requests for configured safes
 */

class QuoteRequestHandler {
  constructor() {
    this.isSubmitting = false;
    this.pendingQuoteData = null; // Store pending quote data for override confirmation
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.attachEventListeners();
    });
  }

  attachEventListeners() {
    const requestBtn = document.getElementById('request-quote-btn');
    if (!requestBtn) return;

    requestBtn.addEventListener('click', () => this.handleQuoteRequest());
    
    // Load configuration button
    const loadBtn = document.getElementById('load-config-btn');
    if (loadBtn) {
      loadBtn.addEventListener('click', () => this.handleLoadConfiguration());
    }

    // If user clicks the "Open Load Config" button (opens modal),
    // auto-fill the email and auto-run load when the visitor is logged in.
    const openLoadConfigBtn = document.getElementById('openLoadConfigBtn');
    if (openLoadConfigBtn) {
      openLoadConfigBtn.addEventListener('click', () => {
        // Delay slightly to allow the modal to become visible (if opened by theme markup)
        setTimeout(async () => {
          try {
            const configuratorDataEl = document.getElementById('configurator-data');
            const configuratorData = configuratorDataEl ? this.parseConfiguratorData(configuratorDataEl) : null;
            const customerEmail = configuratorData?.customerEmail || null;
            if (customerEmail) {
              const emailInput = document.getElementById('load-email');
              if (emailInput) emailInput.value = customerEmail;
              // Run the same flow as the "Show My Configurations" button
              await this.handleLoadConfiguration();
            }
          } catch (err) {
            console.warn('Auto-load configuration failed:', err);
          }
        }, 50);
      });
    }
    
    // Override confirmation button
    const confirmOverrideBtn = document.getElementById('confirmOverrideBtn');
    if (confirmOverrideBtn) {
      confirmOverrideBtn.addEventListener('click', () => this.confirmOverride());
    }
    
    // Check if there's a pending configuration to load from another product page
    this.checkPendingConfigLoad();
  }

  generateQuoteId() {
    // Initialize session tracking Set if not exists
    if (!this.generatedQuoteIds) {
      this.generatedQuoteIds = new Set();
    }

    let attempts = 0;
    const maxAttempts = 100;

    while (attempts < maxAttempts) {
      // Use last 2 digits of timestamp for temporal uniqueness
      const timePart = Date.now() % 100;
      // Generate 3 random digits
      const randomPart = Math.floor(Math.random() * 1000);
      // Combine to create 5-digit number
      const quoteId = String(timePart).padStart(2, '0') + String(randomPart).padStart(3, '0');

      // Check for collision in current session
      if (!this.generatedQuoteIds.has(quoteId)) {
        this.generatedQuoteIds.add(quoteId);
        return quoteId;
      }
      attempts++;
    }

    // Fallback: pure random 5-digit
    const fallbackId = String(Math.floor(10000 + Math.random() * 90000));
    this.generatedQuoteIds.add(fallbackId);
    return fallbackId;
  }

  getConfigurationData() {
    // Get configuration from the configurator instance
    const configuratorEl = document.querySelector('.product-configurator');
    if (!configuratorEl || !window.configuratorInstance) {
      console.error('Configurator instance not found');
      return null;
    }

    const configurator = window.configuratorInstance;
    const state = configurator.state;
    
    // Build configuration summary with LABELS for display
    const config = {
      size: this.getSelectionLabel('size', state.selections),
      exterior_finish: this.getSelectionLabel('exterior_finish', state.selections),
      body_color: this.getSelectionLabel('body_color', state.selections),
      door_color: this.getSelectionLabel('door_color', state.selections),
      single_color: this.getSelectionLabel('color_single', state.selections),
      lock_type: this.getSelectionLabel('lock_type', state.selections),
      lock_color: this.getSelectionLabel('lockgraphic_color', state.selections),
      graphic: this.getSelectionLabel('graphic', state.selections),
      hinge_type: this.getSelectionLabel('hinge_type', state.selections),
      hinge_swing: this.getSelectionLabel('hinge_swing', state.selections),
      hinge_color: this.getSelectionLabel('hinge_color', state.selections),
      accessories: this.getAccessoriesList(state.selections),
      base_price: state.basePrice,
      modifiers_total: state.modifiersTotal,
      total_price: state.totalPrice
    };

    return config;
  }

  getRawConfigurationData() {
    // Get RAW configuration data (codes, not labels) for saving and reloading
    const configuratorEl = document.querySelector('.product-configurator');
    if (!configuratorEl || !window.configuratorInstance) {
      console.error('Configurator instance not found');
      return null;
    }

    const configurator = window.configuratorInstance;
    const state = configurator.state;
    
    // Get product ID and title from configurator data
    const productId = configurator.productId || configurator.data?.productId;
    const productTitle = configurator.productTitle || configurator.data?.productTitle || configurator.data?.productName;
    
    console.log('💾 Saving configuration with product info:', { productId, productTitle });
    
    return {
      selections: JSON.parse(JSON.stringify(state.selections)), // Deep copy
      basePrice: state.basePrice,
      totalPrice: state.totalPrice,
      modifiersTotal: state.modifiersTotal,
      selectedModel: state.selectedModel,
      currentConfiguration: state.currentConfiguration,
      rivetColorOverride: state.rivetColorOverride,
      craneHingeColorOverride: state.craneHingeColorOverride,
      productId: productId,
      productTitle: productTitle,
      productUrl: window.location.href,
      savedAt: new Date().toISOString()
    };
  }

  getSelectionLabel(groupHandle, selections) {
    if (!selections[groupHandle] || !selections[groupHandle][0]) {
      return null;
    }

    const code = selections[groupHandle][0];
    const configurator = window.configuratorInstance;
    const value = configurator.getValueByCode(groupHandle, code);
    
    return value ? value.label : code;
  }

  getAccessoriesList(selections) {
    const accessories = selections['accessory'];
    if (!accessories) return [];

    const configurator = window.configuratorInstance;
    const accessoryList = [];

    if (Array.isArray(accessories)) {
      accessories.forEach(code => {
        const value = configurator.getValueByCode('accessory', code);
        if (value) {
          accessoryList.push(value.label);
        }
      });
    } else if (typeof accessories === 'object') {
      Object.keys(accessories).forEach(code => {
        const value = configurator.getValueByCode('accessory', code);
        if (value) {
          const quantity = accessories[code];
          accessoryList.push(`${value.label}${quantity > 1 ? ` (x${quantity})` : ''}`);
        }
      });
    }

    return accessoryList;
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showError(message) {
    const errorEl = document.getElementById('quote-error-message');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
      setTimeout(() => {
        errorEl.style.display = 'none';
      }, 5000);
    } else {
      alert(message);
    }
  }

  showSuccess(message, quoteId = null) {
    const successEl = document.getElementById('quote-success-message');
    if (successEl) {
      if (quoteId) {
        // Enhanced success message with Quote ID
        successEl.innerHTML = `
          <div class="success-content">
            <p>${message}</p>
            <div class="quote-id-display">
              <strong>Your Quote ID:</strong>
              <div class="quote-id-box">
                <code id="displayedQuoteId">${quoteId}</code>
                <button class="btn-copy-quote" id="copyQuoteIdBtn" title="Copy Quote ID">
                  <i data-lucide="copy" class="icon-xs"></i>
                </button>
              </div>
            </div>
            <p class="quote-id-hint">Save this Quote ID to reload your configuration anytime!</p>
          </div>
        `;
        
        // Add copy functionality
        setTimeout(() => {
          const copyBtn = document.getElementById('copyQuoteIdBtn');
          if (copyBtn) {
            copyBtn.addEventListener('click', () => {
              navigator.clipboard.writeText(quoteId).then(() => {
                copyBtn.innerHTML = '<i data-lucide="check" class="icon-xs"></i>';
                if (typeof lucide !== 'undefined') {
                  lucide.createIcons();
                }
                setTimeout(() => {
                  copyBtn.innerHTML = '<i data-lucide="copy" class="icon-xs"></i>';
                  if (typeof lucide !== 'undefined') {
                    lucide.createIcons();
                  }
                }, 2000);
              }).catch(err => {
                console.error('Failed to copy:', err);
              });
            });
          }
          // Re-render lucide icons
          if (typeof lucide !== 'undefined') {
            lucide.createIcons();
          }
        }, 100);
      } else {
        successEl.textContent = message;
      }
      
      successEl.style.display = 'block';
      setTimeout(() => {
        successEl.style.display = 'none';
      }, 15000); // Show longer for quote ID message
    } else {
      alert(message);
    }
  }

  getNextAvailableSlot(email) {
    // Check which of the 3 slots are available from customer metafields
    // Returns the next available slot number or the oldest one to rotate
    const slots = [];
    
    // Get customer metafield data from the page
    const configuratorDataEl = document.getElementById('configurator-data');
    if (!configuratorDataEl) {
      console.warn('Configurator data not found, falling back to empty array');
      return 1;
    }
    
    try {
      const configuratorData = this.parseConfiguratorData(configuratorDataEl);
      if (!configuratorData) {
        return 1;
      }
      
      for (let i = 1; i <= 3; i++) {
        const fieldName = `savedConfiguration${i}`;
        const configString = configuratorData[fieldName];
        
        if (configString && configString !== 'deleted') {
          try {
            const config = typeof configString === 'string' ? JSON.parse(configString) : configString;
            slots.push({
              slot: i,
              savedAt: new Date(config.savedAt),
              config: config
            });
          } catch (parseErr) {
            console.warn(`Skipping invalid configuration in slot ${i}`, parseErr);
          }
        }
      }
    } catch (e) {
      console.warn('Error parsing configurator data:', e);
      return 1;
    }
    
    // If less than 3 slots filled, return next empty slot
    if (slots.length < 3) {
      return slots.length + 1;
    }
    
    // If all 3 filled, return the oldest one to replace it
    const oldest = slots.reduce((prev, current) => 
      prev.savedAt < current.savedAt ? prev : current
    );
    
    return oldest.slot;
  }

  async getAllSavedConfigurations(email) {
    // Retrieve all saved configurations from Klaviyo profiles by email
    const configs = [];
    
    // First check if customer is logged in and metafields are available
    const configuratorDataEl = document.getElementById('configurator-data');
    if (configuratorDataEl) {
      try {
        const configuratorData = this.parseConfiguratorData(configuratorDataEl);
        if (!configuratorData) {
          return configs;
        }

        const customerEmail = configuratorData.customerEmail;
        
        // If logged in customer matches the email, use metafields
        if (customerEmail && customerEmail.toLowerCase() === email.toLowerCase()) {
          for (let i = 1; i <= 3; i++) {
            const fieldName = `savedConfiguration${i}`;
            const configString = configuratorData[fieldName];
            
            if (configString && configString !== 'deleted') {
              try {
                const config = typeof configString === 'string' ? JSON.parse(configString) : configString;
                configs.push({
                  slot: i,
                  ...config
                });
              } catch (parseErr) {
                console.warn(`Skipping invalid configuration in slot ${i}`, parseErr);
              }
            }
          }
          
          if (configs.length > 0) {
            return configs.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
          }
        }
      } catch (e) {
        console.warn('Error reading metafields:', e);
      }
    }
    
    // If not logged in or no metafields found, we can't retrieve configurations
    // Customer metafields are only accessible when logged in
    console.log('Customer metafields only available when logged in. Customer must log in to view saved configurations.');
    return configs;
  }

  deleteConfiguration(email, slot) {
    // Delete by setting the Klaviyo profile property to empty string
    // This should be called via Klaviyo API
    // Note: The actual API call is made in the modal confirmation handler
    console.log(`Configuration in slot ${slot} should be deleted via Klaviyo API`);
  }

  async deleteConfigurationViaKlaviyo(email, slot, publicApiKey) {
    // Call Klaviyo API to update the profile and clear the configuration slot
    if (!publicApiKey) {
      console.error('Klaviyo API key not found');
      throw new Error('Klaviyo API key required for deletion');
    }

    const configurationKey = `saved_configuration_${slot}`;

    try {
      // Build profile data to clear the configuration slot (same pattern as sendQuoteToKlaviyo)
      const profileData = {
        type: 'profile',
        attributes: {
          email: email,
          properties: {
            // Mark the slot as deleted so it overwrites existing saved data
            [configurationKey]: 'deleted'
          }
        }
      };

      // Build event data
      const eventPayload = {
        data: {
          type: 'event',
          attributes: {
            profile: {
              data: profileData
            },
            metric: {
              data: {
                type: 'metric',
                attributes: {
                  name: 'Safe Configuration Deleted'
                }
              }
            },
            properties: {
              customer_email: email,
              configuration_slot: slot,
              deleted_at: new Date().toISOString()
            }
          }
        }
      };

      console.log('📤 Sending delete event to Klaviyo REST API:', eventPayload);

      // Send event to Klaviyo using REST API (same endpoint as quote saving)
      const response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${publicApiKey}`, {
        method: 'POST',
        headers: {
          'accept': 'application/vnd.api+json',
          'revision': '2026-01-15',
          'content-type': 'application/vnd.api+json'
        },
        body: JSON.stringify(eventPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Klaviyo API Error Response:`, response.status, errorText);
        throw new Error(`Klaviyo API error: ${response.status} - ${errorText}`);
      }

      console.log(`✅ Configuration slot ${slot} cleared via Klaviyo`);
      return true;
    } catch (error) {
      console.error('❌ Error deleting configuration via Klaviyo:', error);
      throw error;
    }
  }

  async checkExistingConfiguration(email, publicApiKey) {
    try {
      // Check if this email already has any saved configurations
      const allConfigs = this.getAllSavedConfigurations(email);
      
      if (allConfigs.length > 0) {
        // Return the most recent one for backward compatibility
        return allConfigs[0];
      }
      
      return null;
    } catch (error) {
      console.error('Error checking existing configuration:', error);
      return null;
    }
  }

  showOverrideConfirmation(existingConfig) {
    const overrideModal = document.getElementById('overrideModal');
    if (!overrideModal) return;

    // Populate existing configuration details
    const quoteIdEl = document.getElementById('existingQuoteId');
    const quoteDateEl = document.getElementById('existingQuoteDate');
    const quotePriceEl = document.getElementById('existingQuotePrice');

    if (quoteIdEl) quoteIdEl.textContent = existingConfig.quoteId || 'Unknown';
    
    if (quoteDateEl && existingConfig.savedAt) {
      const date = new Date(existingConfig.savedAt);
      quoteDateEl.textContent = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
    
    if (quotePriceEl && existingConfig.totalPrice) {
      quotePriceEl.textContent = '$' + existingConfig.totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // Show the modal
    overrideModal.classList.add('active');
    document.body.classList.add('modal-open');
    
    // Re-render lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

  confirmOverride() {
    // Close override modal
    const overrideModal = document.getElementById('overrideModal');
    if (overrideModal) {
      overrideModal.classList.remove('active');
      document.body.classList.remove('modal-open');
    }

    // Proceed with sending the quote (which will override the existing configuration)
    if (this.pendingQuoteData) {
      this.sendQuoteToKlaviyo(this.pendingQuoteData, true); // true = force override
    }
  }

  async handleQuoteRequest() {
    if (this.isSubmitting) {
      console.warn('Quote already submitting, ignoring click.');
      return;
    }

    // Get email input
    const emailInput = document.getElementById('quote-email');
    const nameInput = document.getElementById('quote-name');
    const phoneInput = document.getElementById('quote-phone');
    const notesInput = document.getElementById('quote-notes');

    if (!emailInput) {
      console.error('Email input not found');
      return;
    }

    const email = (emailInput.value || '').trim();
    const name = nameInput ? (nameInput.value || '').trim() : '';
    const phone = phoneInput ? (phoneInput.value || '').trim() : '';
    const notes = notesInput ? (notesInput.value || '').trim() : '';

    // Validate email
    if (!email) {
      this.showError('Please enter your email to receive your quote.');
      emailInput.focus();
      return;
    }

    if (!this.validateEmail(email)) {
      this.showError('Please enter a valid email address.');
      emailInput.focus();
      return;
    }

    // Get configuration data
    const config = this.getConfigurationData();
    if (!config) {
      this.showError('Unable to retrieve configuration. Please try again.');
      return;
    }

    // Update button state
    const requestBtn = document.getElementById('request-quote-btn');
    this.isSubmitting = true;
    const originalText = requestBtn.innerHTML;
    requestBtn.disabled = true;
    requestBtn.innerHTML = '<i data-lucide="loader" class="icon-sm icon-spin"></i> Checking...';

    // Re-render lucide icons for the spinner
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }

    try {
      // Get Klaviyo Public API Key from the script tag
      const klaviyoScripts = Array.from(document.scripts).filter(s => s.src && s.src.includes('klaviyo.com/onsite/js/'));
      let publicApiKey = null;
      
      if (klaviyoScripts.length > 0) {
        // Extract API key from script URL: //static.klaviyo.com/onsite/js/YOUR_KEY/klaviyo.js
        const scriptUrl = klaviyoScripts[0].src;
        const match = scriptUrl.match(/\/onsite\/js\/([^\/]+)\//);
        if (match && match[1]) {
          publicApiKey = match[1];
          console.log('✅ Found Klaviyo Public API Key:', publicApiKey);
        }
      }
      
      if (!publicApiKey) {
        console.error('❌ Could not find Klaviyo Public API Key');
        this.showError('Quote system configuration error. Please contact support.');
        this.isSubmitting = false;
        requestBtn.disabled = false;
        requestBtn.innerHTML = originalText;
        return;
      }

      // Prevent creating more than 3 saved configurations per email
      try {
        const existingConfigs = await this.getAllSavedConfigurations(email);
        if (Array.isArray(existingConfigs) && existingConfigs.length >= 3) {
          this.showError('You already have 3 saved configurations. Please delete an existing configuration to save a new quote, or contact support for assistance.');
          this.isSubmitting = false;
          requestBtn.disabled = false;
          requestBtn.innerHTML = originalText;
          return;
        }
      } catch (e) {
        // If the check fails for any reason, log and continue (do not block quote submission)
        console.warn('Could not verify saved configurations count:', e);
      }

      // Generate unique quote ID
      const quoteId = this.generateQuoteId();
      
      // Store pending data
      this.pendingQuoteData = {
        email,
        name,
        phone,
        notes,
        config,
        quoteId,
        publicApiKey,
        originalText,
        requestBtn
      };
      
      // Proceed with sending quote (supports up to 3 per email)
      await this.sendQuoteToKlaviyo(this.pendingQuoteData);

    } catch (error) {
      console.error('❌ Error in handleQuoteRequest:', error);
      this.showError('There was an error submitting your quote. Please try again.');
      this.isSubmitting = false;
      if (this.pendingQuoteData && this.pendingQuoteData.requestBtn) {
        this.pendingQuoteData.requestBtn.disabled = false;
        this.pendingQuoteData.requestBtn.innerHTML = this.pendingQuoteData.originalText;
      }
    }
  }

  async sendQuoteToKlaviyo(quoteData) {
    const { email, name, phone, notes, config, quoteId, publicApiKey, originalText, requestBtn } = quoteData;
    
    this.isSubmitting = true;
    if (requestBtn) {
      requestBtn.disabled = true;
      requestBtn.innerHTML = '<i data-lucide="loader" class="icon-sm icon-spin"></i> Generating quote PDF...';
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }

    try {
      // Get raw configuration data for saving
      const rawConfig = this.getRawConfigurationData();

      // Generate and upload PDF to Shopify
      let pdfUrl = null;
      try {
        console.log('📄 Attempting to generate and upload PDF...');
        pdfUrl = await this.generateAndUploadQuotePdf(quoteId, email, config);
        if (requestBtn) {
          requestBtn.innerHTML = '<i data-lucide="loader" class="icon-sm icon-spin"></i> Sending quote...';
          if (typeof lucide !== 'undefined') {
            lucide.createIcons();
          }
        }
      } catch (pdfError) {
        console.warn('⚠️ Could not generate PDF, continuing without it:', pdfError.message);
        // Continue without PDF - it's not critical
      }

      // Build profile data
      const profileData = {
        type: 'profile',
        attributes: {
          email: email
        }
      };

      if (name) {
        const nameParts = name.split(' ');
        if (nameParts.length > 1) {
          profileData.attributes.first_name = nameParts[0];
          profileData.attributes.last_name = nameParts.slice(1).join(' ');
        } else {
          profileData.attributes.first_name = name;
        }
        // Format prices for Klaviyo (commas + two decimals)
        const fmtPrice = (n) => {
          if (n === undefined || n === null) return null;
          const num = typeof n === 'string' ? parseFloat(n) : n;
          if (isNaN(num)) return null;
          return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        // Send quote event to Klaviyo using REST API
        const eventPayload = {
          data: {
            type: 'event',
            attributes: {
              profile: { data: profileData },
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: 'Safe Quote Requested' }
                }
              },
              properties: {
                quote_id: quoteId,
                pdf_url: pdfUrl || null,
                customer_email: email,
                customer_name: name,
                customer_phone: phone,
                notes: notes,
                base_price: fmtPrice(config.base_price),
                modifiers_total: fmtPrice(config.modifiers_total),
                total_price: fmtPrice(config.total_price),
                configuration: config,
                raw_configuration: rawConfig
              }
            }
          }
        };

        console.log('📤 Sending quote event to Klaviyo REST API:', eventPayload);

        const response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${publicApiKey}`, {
          method: 'POST',
          headers: {
            'accept': 'application/vnd.api+json',
            'revision': '2026-01-15',
            'content-type': 'application/vnd.api+json'
          },
          body: JSON.stringify(eventPayload)
        });
        // Show the full-screen success state (replaces form)
        this.showQuoteSuccessState(quoteId);

        // Clear form fields for when modal is reopened
        const emailInput = document.getElementById('quote-email');
        const nameInput = document.getElementById('quote-name');
        const phoneInput = document.getElementById('quote-phone');
        const notesInput = document.getElementById('quote-notes');

        if (emailInput) emailInput.value = '';
        if (nameInput) nameInput.value = '';
        if (phoneInput) phoneInput.value = '';
        if (notesInput) notesInput.value = '';
      } else {
        const errorText = await response.text();
        console.error('❌ Klaviyo API Error:', response.status, errorText);

        // Still show success to user (Klaviyo might still process it)
        // Save to localStorage - using slot rotation (up to 3 configurations)
        const nextSlot = this.getNextAvailableSlot(email);
        const storageKey = `saved_config_${email}_${nextSlot}`;
        localStorage.setItem(storageKey, JSON.stringify({
          quoteId: quoteId,
          savedAt: new Date().toISOString(),
          totalPrice: config.total_price,
          rawData: rawConfig
        }));

        // Show the full-screen success state (replaces form)
        this.showQuoteSuccessState(quoteId);

        // Clear form fields for when modal is reopened
        const emailInput = document.getElementById('quote-email');
        const nameInput = document.getElementById('quote-name');
        const phoneInput = document.getElementById('quote-phone');
        const notesInput = document.getElementById('quote-notes');

        if (emailInput) emailInput.value = '';
        if (nameInput) nameInput.value = '';
        if (phoneInput) phoneInput.value = '';
        if (notesInput) notesInput.value = '';
      }

    } catch (error) {
      console.error('❌ Error submitting quote request:', error);
      this.showError('There was an error submitting your quote. Please try again.');
    } finally {
      // Reset button state
      this.isSubmitting = false;
      if (requestBtn) {
        requestBtn.disabled = false;
        requestBtn.innerHTML = originalText;
      }

      // Re-render lucide icons
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }
  }

  showQuoteSuccessState(quoteId) {
    // Hide existing form sections
    const quoteModalBody = document.querySelector('.quote-modal-body');
    const quoteModalFooter = document.querySelector('.quote-modal-footer');

    if (quoteModalBody) quoteModalBody.style.display = 'none';
    if (quoteModalFooter) quoteModalFooter.style.display = 'none';

    // Create and inject success state
    const successHTML = `
      <div class="quote-success-state">
        <div class="quote-success-state__icon">
          <i data-lucide="check-circle" class="icon-success"></i>
        </div>
        <div class="quote-success-state__content">
          <h3 class="quote-success-state__title">Your quote request has been sent</h3>
          <div class="quote-success-state__quote-card">
            <span class="quote-success-state__label">Quote Number</span>
            <span class="quote-success-state__quote-number">${quoteId}</span>
          </div>
          <p class="quote-success-state__message">
            We've sent a confirmation email with your quote details. A dealer will contact you within 24-48 hours.
          </p>
        </div>
        <div class="quote-success-state__actions">
          <button class="btn btn-primary" id="closeSuccessBtn">Close</button>
        </div>
      </div>
    `;

    // Insert after header
    const quoteModalHeader = document.querySelector('.quote-modal-header');
    if (quoteModalHeader) {
      quoteModalHeader.insertAdjacentHTML('afterend', successHTML);

      // Re-render Lucide icons
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }

      // Attach close handler
      const closeBtn = document.getElementById('closeSuccessBtn');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeAndResetQuoteModal());
      }
    }
  }

  closeAndResetQuoteModal() {
    const quoteModal = document.getElementById('quoteModal');
    const successState = document.querySelector('.quote-success-state');
    const quoteModalBody = document.querySelector('.quote-modal-body');
    const quoteModalFooter = document.querySelector('.quote-modal-footer');

    // Close modal
    if (quoteModal) {
      quoteModal.classList.remove('active');
      document.body.classList.remove('modal-open');
    }

    // Remove success state and restore form for future use
    if (successState) successState.remove();
    if (quoteModalBody) quoteModalBody.style.display = '';
    if (quoteModalFooter) quoteModalFooter.style.display = '';
  }

  async handleLoadConfiguration() {
    const emailInput = document.getElementById('load-email');
    
    if (!emailInput) {
      console.error('Load configuration form inputs not found');
      return;
    }

    const email = (emailInput.value || '').trim();

    // Validate email
    if (!email) {
      this.showLoadError('Please enter your email address.');
      emailInput.focus();
      return;
    }

    if (!this.validateEmail(email)) {
      this.showLoadError('Please enter a valid email address.');
      emailInput.focus();
      return;
    }

    // Check if customer is logged in
    const configuratorDataEl = document.getElementById('configurator-data');
    if (!configuratorDataEl) {
      this.showLoadError('You need to be logged in to your account to view your saved configurations. You will be redirected to the login page.');
      setTimeout(() => {
        window.location.href = '/account/login';
      }, 2000);
      return;
    }

    try {
      const configuratorData = this.parseConfiguratorData(configuratorDataEl);
      if (!configuratorData) {
        this.showLoadError('Unable to read your saved configurations. Please refresh and try again.');
        return;
      }
      const customerEmail = configuratorData.customerEmail;

      // If not logged in or email doesn't match logged-in account
      if (!customerEmail) {
        this.showLoadError('You need to be logged in to your account to view your saved configurations. You will be redirected to the login page.');
        setTimeout(() => {
          window.location.href = '/account/login';
        }, 2000);
        return;
      }

      // Email must match logged-in customer
      if (customerEmail.toLowerCase() !== email.toLowerCase()) {
        this.showLoadError('The email you entered does not match your logged-in account. Please log in with the correct account.');
        return;
      }

      // Get all saved configurations for this logged-in email
      const savedConfigs = await this.getAllSavedConfigurations(email);
      
      if (savedConfigs.length === 0) {
        this.showLoadError('No saved configurations found for your account.');
        return;
      }

      // Show saved quotes list for selection
      this.showSavedQuotesList(email, savedConfigs);
    } catch (e) {
      console.error('Error checking login status:', e);
      this.showLoadError('You need to be logged in to your account to view your saved configurations. You will be redirected to the login page.');
      setTimeout(() => {
        window.location.href = '/account/login';
      }, 2000);
    }
  }

  showSavedQuotesList(email, savedConfigs) {
    const loadConfigModal = document.getElementById('loadConfigModal');
    if (!loadConfigModal) return;

    const loadConfigBody = loadConfigModal.querySelector('.load-config-modal-body');
    if (!loadConfigBody) return;

    // Build the saved quotes list HTML
    let html = `
      <div class="quote-limit-notice">
        <strong>Your Saved Configurations:</strong>
        You have ${savedConfigs.length} of 3 saved configurations
      </div>
      <div class="saved-quotes-list">
    `;

    savedConfigs.forEach((config, index) => {
      const date = new Date(config.savedAt);
      const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const priceStr = '$' + config.totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      
      html += `
        <div class="saved-quote-card" data-slot="${config.slot}" data-quote-id="${config.quoteId}">
          <div class="saved-quote-info">
            <div class="saved-quote-id">${config.quoteId}</div>
            <div class="saved-quote-meta">
              <span>📅 ${dateStr}</span>
              <span>💰 ${priceStr}</span>
            </div>
          </div>
          <button class="btn-delete-quote" data-slot="${config.slot}" onclick="event.stopPropagation()">
            Delete
          </button>
        </div>
      `;
    });

    html += '</div>';

    loadConfigBody.innerHTML = html;

    // Add click handlers to load configurations
    loadConfigBody.querySelectorAll('.saved-quote-card').forEach(card => {
      card.addEventListener('click', () => {
        const slot = card.getAttribute('data-slot');
        const quoteId = card.getAttribute('data-quote-id');
        this.loadConfigurationFromSlot(email, slot, quoteId);
      });
    });

    // Add delete handlers
    loadConfigBody.querySelectorAll('.btn-delete-quote').forEach(btn => {
      btn.addEventListener('click', () => {
        const slot = btn.getAttribute('data-slot');
        const quoteId = btn.parentElement.parentElement.getAttribute('data-quote-id');
        this.handleDeleteConfiguration(email, slot, quoteId);
      });
    });
  }

  handleDeleteConfiguration(email, slot, quoteId) {
    // Show the custom delete confirmation modal
    const deleteConfigModal = document.getElementById('deleteConfigModal');
    if (!deleteConfigModal) {
      console.error('Delete modal not found');
      return;
    }

    // Set the quote ID in the modal
    const deleteQuoteIdEl = document.getElementById('deleteQuoteId');
    if (deleteQuoteIdEl) {
      deleteQuoteIdEl.textContent = quoteId;
    }

    // Show the modal
    deleteConfigModal.classList.add('active');
    document.body.classList.add('modal-open');

    // Re-render lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }

    // Store the deletion info for the confirm handler
    deleteConfigModal.dataset.email = email;
    deleteConfigModal.dataset.slot = slot;
    deleteConfigModal.dataset.quoteId = quoteId;
  }

  loadConfigurationFromSlot(email, slot, quoteId) {
    // Get customer metafield data from the page
    const configuratorDataEl = document.getElementById('configurator-data');
    if (!configuratorDataEl) {
      this.showLoadError('Unable to access saved configurations.');
      return;
    }

    try {
      const configuratorData = this.parseConfiguratorData(configuratorDataEl);
      if (!configuratorData) {
        this.showLoadError('Unable to access saved configurations.');
        return;
      }
      const fieldName = `savedConfiguration${slot}`;
      const configString = configuratorData[fieldName];
      
      if (!configString || configString === 'deleted') {
        this.showLoadError('Configuration not found. It may have been deleted.');
        return;
      }
      
      let savedConfig = configString;
      if (typeof configString === 'string') {
        try {
          savedConfig = JSON.parse(configString);
        } catch (parseErr) {
          console.error('❌ Error parsing saved configuration JSON:', parseErr);
          this.showLoadError('Configuration data is corrupted. Please save again.');
          return;
        }
      }
      
      console.log('✅ Configuration found:', savedConfig);

      // Validate that the saved configuration is for the current product
      if (window.configuratorInstance && savedConfig.rawData) {
        const configurator = window.configuratorInstance;
        const currentProductId = configurator.productId || configurator.data?.productId;
        const currentProductTitle = configurator.productTitle || configurator.data?.productTitle || configurator.data?.productName;
        
        // Handle both new format (productId at root) and old format (productId in selectedModel)
        const savedProductId = savedConfig.rawData.productId || 
                               savedConfig.rawData.selectedModel?.productId;
        const savedProductTitle = savedConfig.rawData.productTitle || 
                                 savedConfig.rawData.selectedModel?.name;
        const savedProductUrl = savedConfig.rawData.productUrl;

        console.log('🔍 Product validation:', {
          current: { id: currentProductId, title: currentProductTitle },
          saved: { id: savedProductId, title: savedProductTitle, url: savedProductUrl }
        });

        // Check if product IDs match (only validate if both IDs are available)
        if (savedProductId && currentProductId && String(savedProductId) !== String(currentProductId)) {
          // Product mismatch - redirect to the correct product page if URL is available
          if (savedProductUrl) {
            this.showLoadError(`This configuration is for "${savedProductTitle || 'another product'}". Redirecting you to the correct product page...`);
            
            // Close the modal before redirecting
            const loadConfigModal = document.getElementById('loadConfigModal');
            if (loadConfigModal) {
              loadConfigModal.classList.remove('active');
              document.body.classList.remove('modal-open');
            }
            
            // Redirect after a short delay to allow user to see the message
            setTimeout(() => {
              // Store the configuration data to load on the new page
              sessionStorage.setItem('pendingConfigLoad', JSON.stringify({
                email,
                slot,
                quoteId,
                timestamp: Date.now()
              }));
              
              window.location.href = savedProductUrl;
            }, 2000);
            
            return;
          } else {
            // No URL available, show error
            this.showLoadError(`Error: This saved configuration is for "${savedProductTitle || 'a different product'}", not for the current product "${currentProductTitle}". Please navigate to the correct product page to load this configuration.`);
            return;
          }
        }

        // Product matches or no product ID - proceed with loading
        window.configuratorInstance.loadSavedConfiguration(savedConfig.rawData);
        
        // Close the load modal
        const loadConfigModal = document.getElementById('loadConfigModal');
        if (loadConfigModal) {
          loadConfigModal.classList.remove('active');
          document.body.classList.remove('modal-open');
        }
        
        this.showLoadSuccess('Configuration loaded successfully!');
      } else {
        this.showLoadError('Unable to load configuration. Please refresh the page and try again.');
      }
    } catch (error) {
      console.error('❌ Error loading configuration:', error);
      this.showLoadError('There was an error loading your configuration. Please try again.');
    }
  }

  showLoadError(message) {
    const errorEl = document.getElementById('load-error-message');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
      setTimeout(() => {
        errorEl.style.display = 'none';
      }, 5000);
    } else {
      alert(message);
    }
  }

  showLoadSuccess(message) {
    const successEl = document.getElementById('load-success-message');
    if (successEl) {
      successEl.textContent = message;
      successEl.style.display = 'block';
      setTimeout(() => {
        successEl.style.display = 'none';
      }, 3000);
    }
  }

  checkPendingConfigLoad() {
    // Check if we were redirected here to load a configuration
    const pendingLoadStr = sessionStorage.getItem('pendingConfigLoad');
    if (!pendingLoadStr) return;

    try {
      const pendingLoad = JSON.parse(pendingLoadStr);
      
      // Check if the pending load is recent (within last 30 seconds)
      if (Date.now() - pendingLoad.timestamp > 30000) {
        sessionStorage.removeItem('pendingConfigLoad');
        return;
      }

      // Clear the pending load
      sessionStorage.removeItem('pendingConfigLoad');

      console.log('📥 Loading pending configuration:', pendingLoad);

      // Auto-load the configuration after a short delay to ensure configurator is ready
      setTimeout(() => {
        this.loadConfigurationFromSlot(pendingLoad.email, pendingLoad.slot, pendingLoad.quoteId);
        this.showLoadSuccess('Configuration loaded successfully!');
      }, 500);
    } catch (error) {
      console.error('❌ Error processing pending config load:', error);
      sessionStorage.removeItem('pendingConfigLoad');
    }
  }

  sanitizeConfiguratorJson(rawText) {
    // Quote bare deleted sentinel values for savedConfiguration slots
    return rawText.replace(/("savedConfiguration\d+"\s*:\s*)(deleted)(\s*[},])/gi, '$1"deleted"$3');
  }

  parseConfiguratorData(configuratorDataEl) {
    if (!configuratorDataEl || !configuratorDataEl.textContent) return null;

    const rawText = configuratorDataEl.textContent.trim();
    if (!rawText) return null;

    try {
      return JSON.parse(rawText);
    } catch (err) {
      // Attempt to sanitize common invalid tokens (e.g., bare deleted values)
      const sanitized = this.sanitizeConfiguratorJson(rawText);
      if (sanitized !== rawText) {
        try {
          console.warn('Sanitized configurator data before parsing');
          return JSON.parse(sanitized);
        } catch (err2) {
          console.error('Failed to parse configurator data after sanitization', err2);
          return null;
        }
      }

      console.error('Failed to parse configurator data', err);
      return null;
    }
  }

  /**
   * Wait for Klaviyo to be fully loaded and ready
   * @param {number} timeout - Maximum time to wait in milliseconds
   * @returns {Promise<boolean>} - True if Klaviyo is ready, false if timeout
   */
  async waitForKlaviyo(timeout = 5000) {
    const startTime = Date.now();
    
    while (Date.now() - startTime < timeout) {
      // Check if Klaviyo object exists
      if (typeof window.klaviyo !== 'undefined') {
        // Check if methods exist
        if (typeof window.klaviyo.identify === 'function' && 
            typeof window.klaviyo.track === 'function') {
          console.log('✅ Klaviyo loaded successfully', {
            klaviyo: typeof window.klaviyo,
            identify: typeof window.klaviyo.identify,
            track: typeof window.klaviyo.track,
            timeToLoad: Date.now() - startTime + 'ms'
          });
          return true;
        }
      }
      
      // Wait 100ms before checking again
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Timeout reached
    console.error('❌ Klaviyo did not load within timeout', {
      timeout,
      klaviyoExists: typeof window.klaviyo !== 'undefined',
      klaviyoType: typeof window.klaviyo,
      identifyType: typeof window.klaviyo?.identify,
      trackType: typeof window.klaviyo?.track
    });
    
    return false;
  }

  /**
   * Generate a quote PDF and upload it to Shopify Files
   * @param {string} quoteId - Quote ID
   * @param {string} email - Customer email
   * @param {object} config - Configuration data
   * @returns {Promise<string|null>} - PDF URL or null if upload fails
   */
  async generateAndUploadQuotePdf(quoteId, email, config) {
    try {
      let pdfBlob = null;

      // Prefer generating the official 'Safe Configuration' PDF by delegating
      // to the configurator's `saveConfigurationPDF` in quiet mode. This ensures
      // the layout matches the Download Configuration PDF exactly.
      if (window.configuratorInstance && typeof window.configuratorInstance.saveConfigurationPDF === 'function') {
        try {
          const resultObj = await window.configuratorInstance.saveConfigurationPDF(true, `Quote ${quoteId}`);
          pdfBlob = resultObj?.blob || null;
        } catch (delegationError) {
          console.warn('⚠️ Delegation to saveConfigurationPDF failed:', delegationError.message);
        }
      }

      // Fallback to inline PDF generator if delegation failed or not available
      if (!pdfBlob) {
        console.warn('⚠️ Using inline PDF generator as fallback');
        await this.loadPdfLibraries();

        const jsPDF = window.jsPDF || (window.jspdf && window.jspdf.jsPDF) || window.jspdf;
        if (!jsPDF) {
          throw new Error('jsPDF not available for inline generation');
        }

        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(16);
        doc.text('Quote Request', 10, 20);
        doc.setFontSize(12);
        doc.text(`Quote ID: ${quoteId}`, 10, 40);
        doc.text(`Email: ${email}`, 10, 50);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 10, 60);

        // Add configuration details
        let y = 80;
        doc.text('Configuration:', 10, y);
        y += 10;
        if (config.size) doc.text(`Size: ${config.size}`, 10, y += 10);
        if (config.exterior_finish) doc.text(`Exterior Finish: ${config.exterior_finish}`, 10, y += 10);
        if (config.body_color) doc.text(`Body Color: ${config.body_color}`, 10, y += 10);
        if (config.door_color) doc.text(`Door Color: ${config.door_color}`, 10, y += 10);
        if (config.lock_type) doc.text(`Lock Type: ${config.lock_type}`, 10, y += 10);
        if (config.hinge_type) doc.text(`Hinge Type: ${config.hinge_type}`, 10, y += 10);
        if (config.accessories && config.accessories.length > 0) {
          doc.text('Accessories:', 10, y += 10);
          config.accessories.forEach(accessory => {
            doc.text(`- ${accessory}`, 15, y += 10);
          });
        }
        if (config.total_price) doc.text(`Total Price: $${config.total_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 10, y += 10);

        // Try to load and render the company logo centered at the bottom
        try {
          const loadLogoDataURL = async () => {
            const url = 'https://cdn.shopify.com/s/files/1/0530/6023/9548/files/norcal-logo.png?v=1769648945';
            try {
              const resp = await fetch(url);
              if (!resp.ok) return null;
              const blob = await resp.blob();
              return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            } catch (e) {
              return null;
            }
          };

          const logoDataUrl = await loadLogoDataURL();
          if (logoDataUrl) {
            const pageWidth = 210; // A4 width mm
            const pageHeight = 297;
            const margin = 20;
            const img = new Image();
            img.src = logoDataUrl;
            await new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; });
            const logoWidthMm = 40;
            const aspect = (img.naturalWidth && img.naturalHeight) ? (img.naturalHeight / img.naturalWidth) : 0.3;
            const logoHeightMm = aspect ? (logoWidthMm * aspect) : 12;
            const logoX = (pageWidth - logoWidthMm) / 2;
            const logoY = pageHeight - margin - logoHeightMm - 4;
            try { doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidthMm, logoHeightMm); } catch (e) { console.warn('Could not add logo to fallback PDF:', e); }
          }
        } catch (e) {
          console.warn('Logo render skipped in fallback PDF:', e);
        }

        pdfBlob = doc.output('blob');
      }

      // Upload the PDF blob
      const reader = new FileReader();
      return await new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          try {
            const base64String = reader.result.split(',')[1];
            const shopDomain = (window.Shopify && window.Shopify.shop) || (typeof Shopify !== 'undefined' && Shopify.shop) || window.location.hostname;

            const graphqlQuery = `
              mutation UploadQuotePdf(
                $pdfBase64: String!
                $filename: String!
                $quoteId: String
                $email: String
                $shopDomain: String!
              ) {
                uploadQuotePdf(
                  pdfBase64: $pdfBase64
                  filename: $filename
                  quoteId: $quoteId
                  email: $email
                  shopDomain: $shopDomain
                ) {
                  success
                  errors { message }
                  result
                }
              }
            `;

            const graphqlBody = {
              query: graphqlQuery,
              variables: {
                pdfBase64: base64String,
                filename: `quote-${quoteId}.pdf`,
                quoteId: quoteId,
                email: email,
                shopDomain: shopDomain
              }
            };

            const uploadResponse = await fetch('/apps/meta-variant-builder-api', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(graphqlBody)
            });

            if (!uploadResponse.ok) {
              const text = await uploadResponse.text();
              console.error('❌ PDF upload failed (non-200):', uploadResponse.status, text);
              reject(new Error(`Upload failed: ${uploadResponse.status}`));
              return;
            }

            const uploadData = await uploadResponse.json();

            // GraphQL-level errors
            if (uploadData.errors && uploadData.errors.length > 0) {
              console.error('❌ GraphQL errors:', uploadData.errors);
              reject(new Error(uploadData.errors[0].message || 'Upload failed'));
              return;
            }

            const actionResult = uploadData.data?.uploadQuotePdf;

            // Action-level errors (from Gadget global action)
            if (actionResult?.errors && actionResult.errors.length > 0) {
              console.error('❌ Action errors:', actionResult.errors);
              reject(new Error(actionResult.errors[0].message || 'Upload failed'));
              return;
            }

            // The actual payload returned by your `run` is wrapped in `result`
            const result = actionResult?.result;

            if (actionResult?.success && result?.quoteId) {
              // We received the quote record id — now request a temporary signed URL
              const getUrlQuery = `
                mutation GetQuoteUrl($quoteId: String!) {
                  getQuoteUrl(quoteId: $quoteId) {
                    success
                    errors { message }
                    result
                  }
                }
              `;

              const getUrlBody = {
                query: getUrlQuery,
                variables: { quoteId: result.quoteId }
              };

              const urlResponse = await fetch('/apps/meta-variant-builder-api', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(getUrlBody)
              });

              if (!urlResponse.ok) {
                const text = await urlResponse.text();
                console.error('❌ GetQuoteUrl failed (non-200):', urlResponse.status, text);
                reject(new Error(`GetQuoteUrl failed: ${urlResponse.status}`));
                return;
              }

              const urlData = await urlResponse.json();
              if (urlData.errors && urlData.errors.length > 0) {
                console.error('❌ GraphQL errors (getUrl):', urlData.errors);
                reject(new Error(urlData.errors[0].message || 'GetQuoteUrl failed'));
                return;
              }

              const urlAction = urlData.data?.getQuoteUrl;
              if (urlAction?.errors && urlAction.errors.length > 0) {
                console.error('❌ Action errors (getUrl):', urlAction.errors);
                reject(new Error(urlAction.errors[0].message || 'GetQuoteUrl failed'));
                return;
              }

              const urlResult = urlAction?.result;
              const pdfUrl = urlResult?.url || urlResult;
              if (!pdfUrl) {
                console.error('❌ No URL returned for quote:', result.quoteId);
                reject(new Error('No URL returned for quote'));
                return;
              }

              console.log('✅ PDF signed URL:', pdfUrl);
              resolve(pdfUrl);
            } else {
              console.error('❌ Upload failed:', actionResult);
              reject(new Error('Upload failed'));
            }
          } catch (error) {
            console.error('❌ Error processing PDF:', error);
            reject(error);
          }
        };

        reader.onerror = () => {
          reject(new Error('Failed to read PDF blob'));
        };

        reader.readAsDataURL(pdfBlob);
      });
    } catch (error) {
      console.error('❌ Error generating/uploading PDF:', error);
      throw error;
    }
  }

  async loadPdfLibraries() {
    // Check if jsPDF is already available in any form
    const getJsPDF = () => {
      if (window.jsPDF) return window.jsPDF;
      if (window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
      if (window.jspdf) return window.jspdf;
      return null;
    };

    // Load jsPDF if not available
    if (!getJsPDF()) {
      await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      
      // jsPDF UMD might need a moment to initialize
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Log what's available
      console.log('🔍 After loading jsPDF:', {
        'window.jsPDF': typeof window.jsPDF,
        'window.jspdf': typeof window.jspdf,
        'window.jspdf.jsPDF': typeof window.jspdf?.jsPDF
      });
      
      // If still not available, try alternative CDN
      if (!getJsPDF()) {
        console.warn('⚠️ jsPDF not available from primary CDN, trying alternative...');
        await this.loadScript('https://unpkg.com/jspdf@latest/dist/jspdf.umd.min.js');
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log('🔍 After alternative CDN:', {
          'window.jsPDF': typeof window.jsPDF,
          'window.jspdf': typeof window.jspdf,
          'window.jspdf.jsPDF': typeof window.jspdf?.jsPDF
        });
      }
    }

    // Load html2pdf
    if (!window.html2pdf) {
      await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js');
      
      // html2pdf might need a moment to initialize
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // If still not available, try alternative
      if (!window.html2pdf) {
        console.warn('⚠️ html2pdf not available from primary CDN, trying alternative...');
        await this.loadScript('https://unpkg.com/html2pdf.js@latest/dist/html2pdf.bundle.min.js');
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Verify jsPDF is loaded (in any form)
    const jsPDFLib = getJsPDF();
    if (!jsPDFLib) {
      console.error('❌ jsPDF not found. Available globals:', Object.keys(window).filter(k => k.toLowerCase().includes('pdf')));
      throw new Error('Failed to load jsPDF library');
    }
    
    console.log('✅ jsPDF library detected:', typeof jsPDFLib);
    
    if (!window.html2pdf) {
      console.warn('⚠️ html2pdf not loaded, but continuing with jsPDF only');
    }
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.type = 'text/javascript';
      script.async = true;
      
      script.onload = () => {
        console.log(`✅ Script loaded: ${src}`);
        resolve();
      };
      
      script.onerror = () => {
        console.error(`❌ Failed to load script: ${src}`);
        reject(new Error(`Failed to load script: ${src}`));
      };
      
      document.head.appendChild(script);
    });
  }
}

// Initialize quote request handler
window.quoteHandler = new QuoteRequestHandler();

// Add global helper function for debugging
window.checkKlaviyoStatus = function() {
  console.log('🔍 Klaviyo Status Check:');
  console.log('- window.klaviyo exists:', typeof window.klaviyo !== 'undefined');
  console.log('- window.klaviyo type:', typeof window.klaviyo);
  
  if (typeof window.klaviyo !== 'undefined') {
    console.log('- klaviyo.identify exists:', typeof window.klaviyo.identify === 'function');
    console.log('- klaviyo.track exists:', typeof window.klaviyo.track === 'function');
    console.log('- klaviyo object:', window.klaviyo);
  }
  
  // Check for Klaviyo script in DOM
  const klaviyoScripts = Array.from(document.scripts).filter(s => s.src && s.src.includes('klaviyo'));
  console.log('- Klaviyo scripts found:', klaviyoScripts.length);
  klaviyoScripts.forEach((script, i) => {
    console.log(`  Script ${i + 1}:`, script.src);
  });
  
  // Check for loading errors
  console.log('\nℹ️  If klaviyo is undefined, check:');
  console.log('  1. Verify Public API Key in section settings');
  console.log('  2. Check Network tab for klaviyo.js (should be 200, not 404)');
  console.log('  3. Look for any console errors when page loads');
};

console.log('💡 Run checkKlaviyoStatus() in console to debug Klaviyo integration');
